import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ShieldCheck,
  Camera,
  FolderLock,
  Users,
  ArrowRight,
  CheckCircle2,
  FileText,
  Sparkles,
  HardHat,
  Clock,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1772442198624-4fc4d7281e89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxjb250cmFjdG9yJTIwaG9tZSUyMGluc3BlY3Rpb258ZW58MHx8fHwxNzc3NzMyNDIzfDA&ixlib=rb-4.1.0&q=85";
const SCAN_IMG =
  "https://images.unsplash.com/photo-1760160135007-52e99ededaad?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwzfHxyb29mJTIwaW5zcGVjdGlvbiUyMGRhbWFnZXxlbnwwfHx8fDE3Nzc3MzI0Mjd8MA&ixlib=rb-4.1.0&q=85";
const VAULT_IMG =
  "https://images.unsplash.com/photo-1721132537184-5494c01ed87f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxob3VzZSUyMGJsdWVwcmludCUyMGRvY3VtZW50fGVufDB8fHx8MTc3NzczMjQyN3ww&ixlib=rb-4.1.0&q=85";

export default function LandingPage() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);
  return (
    <div className="bg-[#F9F8F6]" data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-3 py-1 text-xs text-gray-700 mb-6">
              <Sparkles className="w-3.5 h-3.5 text-[#C25934]" />
              AI-guided claims for homeowners & businesses
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Don't fight your insurer alone.
              <br />
              <span className="text-[#1E3F33]">We'll evaluate your loss</span>{" "}
              in 5 minutes.
            </h1>
            <p className="mt-6 text-lg text-gray-600 max-w-2xl leading-relaxed">
              ClaimScope is the public adjusting platform that walks you through
              every photo, document, and form — so you get paid what your policy
              actually owes you.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-start-claim-button">
                <Button className="btn-primary rounded-full px-7 py-6 text-base">
                  Start my claim <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/signup?role=contractor" data-testid="hero-contractor-cta">
                <Button
                  variant="outline"
                  className="rounded-full px-7 py-6 text-base border-[#1E3F33] text-[#1E3F33] hover:bg-[#1E3F33] hover:text-[#F9F8F6]"
                >
                  I'm a contractor
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#1E3F33]" />
                No upfront fees
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#1E3F33]" />
                Licensed adjusters
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#1E3F33]" />
                Vetted contractor network
              </div>
            </div>

            {stats && (stats.signatures_total > 0 || stats.claims_filed_total > 0) && (
              <div
                className="mt-8 flex flex-wrap gap-6"
                data-testid="public-stats-row"
              >
                {stats.signatures_this_week > 0 && (
                  <div className="rounded-xl border border-[#E5E7EB] bg-white px-5 py-3">
                    <div className="text-2xl font-['Outfit'] font-semibold text-[#1E3F33]" data-testid="stat-signatures-week">
                      {stats.signatures_this_week.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Verified signatures this week
                    </div>
                  </div>
                )}
                {stats.signatures_total > 0 && (
                  <div className="rounded-xl border border-[#E5E7EB] bg-white px-5 py-3">
                    <div className="text-2xl font-['Outfit'] font-semibold text-[#1E3F33]" data-testid="stat-signatures-total">
                      {stats.signatures_total.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Proofs of Loss e-signed
                    </div>
                  </div>
                )}
                {stats.claims_filed_total > 0 && (
                  <div className="rounded-xl border border-[#E5E7EB] bg-white px-5 py-3">
                    <div className="text-2xl font-['Outfit'] font-semibold text-[#1E3F33]" data-testid="stat-claims-total">
                      {stats.claims_filed_total.toLocaleString()}
                    </div>
                    <div className="text-xs text-gray-500 uppercase tracking-wide">
                      Claims filed on ClaimScope
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-2xl overflow-hidden border border-[#E5E7EB] shadow-xl shadow-[#1E3F33]/10">
              <img
                src={HERO_IMG}
                alt="Inspection"
                className="w-full h-[480px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0b1f17]/55 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 bg-white/85 backdrop-blur-md rounded-xl border border-white/60 p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 rounded-full bg-[#C25934] animate-pulse" />
                  <p className="text-xs font-medium tracking-wide text-gray-700 uppercase">
                    Live AI Scan
                  </p>
                </div>
                <p className="font-['Outfit'] text-lg font-semibold">
                  Moisture spot detected — ceiling NW corner
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Severity: moderate · Tag added to claim #4821
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-t border-[#E5E7EB] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-2xl">
            <p className="text-sm uppercase tracking-widest text-[#C25934] font-medium mb-3">
              How it works
            </p>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight">
              From loss to settlement in three guided steps.
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              {
                step: "01",
                title: "Smart intake",
                body:
                  "Three-step wizard captures your identity, policy, and event — no flat 30-field form.",
                icon: FileText,
              },
              {
                step: "02",
                title: "AI photo capture",
                body:
                  "Camera overlay tells you exactly what to shoot. AI auto-tags moisture, soot, shingle uplift.",
                icon: Camera,
              },
              {
                step: "03",
                title: "Adjuster review",
                body:
                  "Our licensed adjusters open your case, match a vetted contractor, and negotiate the payout.",
                icon: ShieldCheck,
              },
            ].map((s) => (
              <div
                key={s.step}
                className="rounded-xl border border-[#E5E7EB] bg-[#F9F8F6] p-8 hover:border-[#1E3F33] transition-colors"
                data-testid={`how-step-${s.step}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <span className="font-['Outfit'] text-3xl font-semibold text-[#1E3F33]">
                    {s.step}
                  </span>
                  <s.icon className="w-6 h-6 text-[#C25934]" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold mb-2">
                  {s.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section id="features" className="bg-[#F9F8F6]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight max-w-2xl">
            Everything an adjuster wishes you'd send the first time.
          </h2>

          <div className="mt-12 grid md:grid-cols-12 gap-6">
            {/* AI camera */}
            <div className="md:col-span-7 rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden">
              <div className="relative h-72">
                <img
                  src={SCAN_IMG}
                  alt="AI scan"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-[#0b1f17]/35" />
                <div className="absolute inset-6 border-2 border-[#C25934]/70 rounded-xl pointer-events-none">
                  <span className="absolute -top-px -left-px w-6 h-6 border-t-2 border-l-2 border-[#C25934]" />
                  <span className="absolute -top-px -right-px w-6 h-6 border-t-2 border-r-2 border-[#C25934]" />
                  <span className="absolute -bottom-px -left-px w-6 h-6 border-b-2 border-l-2 border-[#C25934]" />
                  <span className="absolute -bottom-px -right-px w-6 h-6 border-b-2 border-r-2 border-[#C25934]" />
                </div>
                <div className="absolute top-4 left-4 bg-black/55 backdrop-blur text-white text-xs px-3 py-1.5 rounded-full">
                  Aligning ceiling corner…
                </div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-2 text-xs text-[#C25934] uppercase tracking-widest font-medium mb-2">
                  <Camera className="w-4 h-4" /> AI photo capture
                </div>
                <h3 className="font-['Outfit'] text-2xl font-semibold mb-2">
                  Ghost overlays. Real-time damage tags. GPS-locked metadata.
                </h3>
                <p className="text-gray-600">
                  Your camera becomes an evidence-grade tool. Every shot is
                  geo-stamped to prevent fraud denials, and our model flags
                  damage as you shoot.
                </p>
              </div>
            </div>

            {/* Document vault */}
            <div className="md:col-span-5 rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden">
              <div
                className="h-72 bg-cover bg-center"
                style={{ backgroundImage: `url(${VAULT_IMG})` }}
              />
              <div className="p-8">
                <div className="flex items-center gap-2 text-xs text-[#C25934] uppercase tracking-widest font-medium mb-2">
                  <FolderLock className="w-4 h-4" /> Document vault
                </div>
                <h3 className="font-['Outfit'] text-2xl font-semibold mb-2">
                  Drag, drop, done.
                </h3>
                <p className="text-gray-600">
                  Xactimate exports, declarations pages, and prior history —
                  all in one secure place.
                </p>
              </div>
            </div>

            {/* Carrier guides */}
            <div className="md:col-span-5 rounded-2xl border border-[#E5E7EB] bg-[#1E3F33] text-[#F9F8F6] p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#C25934] font-medium mb-4">
                <FileText className="w-4 h-4" /> Carrier guides
              </div>
              <h3 className="font-['Outfit'] text-2xl font-semibold mb-3">
                Instant, carrier-specific instructions.
              </h3>
              <p className="text-[#F9F8F6]/80 leading-relaxed">
                Sign up with State Farm, Allstate, USAA — or any of 60+
                carriers — and we hand you the exact playbook for what they ask
                for, including Proof of Loss timing.
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                {[
                  "State Farm",
                  "Allstate",
                  "USAA",
                  "GEICO",
                  "Liberty",
                  "Travelers",
                ].map((c) => (
                  <span
                    key={c}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/10 border border-white/20"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            {/* Contractor matching */}
            <div className="md:col-span-7 rounded-2xl border border-[#E5E7EB] bg-white p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#C25934] font-medium mb-4">
                <Users className="w-4 h-4" /> Contractor co-pilot
              </div>
              <h3 className="font-['Outfit'] text-2xl font-semibold mb-3">
                A vetted contractor in your zip — uploading their estimate
                straight into your file.
              </h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                When you submit a claim, ClaimScope matches a qualified
                contractor by zip code and damage type. They upload Xactimate
                directly, and our team is notified the moment it lands.
              </p>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { icon: HardHat, label: "Licensed & vetted" },
                  { icon: Clock, label: "Same-day response" },
                  { icon: ShieldCheck, label: "No commission games" },
                ].map((b) => (
                  <div key={b.label} className="flex flex-col gap-2">
                    <b.icon className="w-5 h-5 text-[#1E3F33]" />
                    <p className="text-sm text-gray-700">{b.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTRACTORS CTA */}
      <section
        id="contractors"
        className="bg-white border-t border-[#E5E7EB]"
      >
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm uppercase tracking-widest text-[#C25934] font-medium mb-3">
              For contractors
            </p>
            <h2 className="font-['Outfit'] text-3xl md:text-4xl font-semibold tracking-tight">
              Quality leads. No bidding wars.
            </h2>
            <p className="mt-5 text-gray-600 leading-relaxed text-lg">
              We send you pre-qualified claims in your zip — only homeowners
              who've already passed our adjuster intake. Upload your estimate
              once, get paid faster.
            </p>
            <Link to="/signup?role=contractor" data-testid="contractors-cta">
              <Button className="btn-primary rounded-full px-7 py-6 mt-8">
                Apply to the network <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
          <div className="relative">
            <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9F8F6] p-8">
              <div className="flex items-center justify-between mb-6">
                <p className="text-xs uppercase tracking-widest text-gray-500">
                  Available leads
                </p>
                <span className="text-xs text-[#1E3F33] font-medium">
                  Live
                </span>
              </div>
              {[
                { z: "33101", t: "Water — kitchen ceiling", a: "$8,400" },
                { z: "10004", t: "Wind — roof shingles", a: "$14,200" },
                { z: "75201", t: "Fire — smoke damage", a: "$22,000" },
              ].map((l) => (
                <div
                  key={l.z}
                  className="flex items-center justify-between py-4 border-b border-[#E5E7EB] last:border-0"
                >
                  <div>
                    <p className="font-medium">{l.t}</p>
                    <p className="text-sm text-gray-500">Zip {l.z}</p>
                  </div>
                  <span className="font-['Outfit'] font-semibold text-[#1E3F33]">
                    {l.a}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#1E3F33] text-[#F9F8F6]/80">
        <div className="max-w-7xl mx-auto px-6 py-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            <span className="font-['Outfit'] font-semibold text-[#F9F8F6]">
              ClaimScope
            </span>
          </div>
          <p className="text-sm">
            Public adjusting, modernized. © {new Date().getFullYear()}.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <a
              href="/privacy"
              className="hover:text-[#F9F8F6] transition-colors"
              data-testid="footer-privacy-link"
            >
              Privacy
            </a>
            <a
              href="/terms"
              className="hover:text-[#F9F8F6] transition-colors"
              data-testid="footer-terms-link"
            >
              Terms
            </a>
            <a
              href="mailto:hello@claimscope.app"
              className="hover:text-[#F9F8F6] transition-colors"
              data-testid="footer-contact-link"
            >
              Contact
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
